import { createTheme } from '@mui/material/styles';
import { dialogTitleClasses } from '@mui/material/DialogTitle';
import { COLORS } from './constants';
const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      tb: 700,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
  palette: {
    primary: {
      main: COLORS.PRIMARY,
      light: '#1e3a8a',
      dark: '#0a1a2e',
      contrastText: COLORS.WHITE,
    },
    secondary: {
      main: COLORS.SECONDARY,
      light: '#fb923c',
      dark: '#ea580c',
      contrastText: COLORS.WHITE,
    },
    background: {
      default: COLORS.LIGHT_GREY,
      paper: COLORS.WHITE,
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
    error: {
      main: COLORS.ERROR,
    },
    warning: {
      main: COLORS.WARNING,
    },
    success: {
      main: COLORS.SUCCESS,
    },
    // Custom colors for the dashboard
    dashboard: {
      header: COLORS.PRIMARY,
      accent: COLORS.SECONDARY,
      lightGrey: '#f1f5f9',
      white: COLORS.WHITE,
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 700,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
      lineHeight: 1.3,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.4,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.6,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.875rem',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButtonBase: {
      styleOverrides: {
        root: {
          '&:focus': { outline: 'none', boxShadow: 'none' },
          '&:focus-visible': { outline: 'none', boxShadow: 'none' },
          '&.Mui-focusVisible': { outline: 'none', boxShadow: 'none' },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:focus': { outline: 'none', boxShadow: 'none' },
          '&:focus-visible': { outline: 'none', boxShadow: 'none' },
          '&.Mui-focusVisible': { outline: 'none', boxShadow: 'none' },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.23)',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          '&:focus': { outline: 'none' },
          '&:focus-visible': { outline: 'none' },
          '&.Mui-focusVisible': { outline: 'none' },
        },
      },
    },
    MuiDialog: {
      defaultProps: {
        disableEscapeKeyDown: true,
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        // MUI's own DialogContent styles force `padding-top: 0` on
        // `.MuiDialogTitle-root + .MuiDialogContent-root`. That's an
        // adjacent-sibling selector (2 classes), higher specificity than a
        // plain `sx`-generated class (1 class), so a dialog's own `sx`
        // padding-top is silently dropped whenever DialogContent follows a
        // DialogTitle — regardless of what that sx sets. Overriding the same
        // selector here (theme overrides are injected after MUI's own
        // defaults, so equal-specificity ties go to this rule) restores the
        // normal, non-zeroed top padding globally.
        root: {
          [`.${dialogTitleClasses.root} + &`]: {
            paddingTop: 20,
          },
        },
      },
    },
    MuiTooltip: {
      defaultProps: {
        slotProps: {
          transition: { timeout: 0 },
        },
      },
      styleOverrides: {
        popper: {
          transition: 'none !important',
          '& > div': {
            transition: 'none !important',
          },
        },
        arrow: {
          transition: 'none !important',
        },
      },
    },
    MuiPickersPopper: {
      defaultProps: {
        slotProps: {
          desktopTransition: { timeout: 0 },
        },
      },
      styleOverrides: {
        root: {
          transition: 'none !important',
          '& > div': {
            transition: 'none !important',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          padding: '8px 16px',
          borderRadius: '8px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
          },
        },
        contained: {
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          background: '#0D213B', // Exact dark blue header
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
          borderRadius: '12px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)': {
            backgroundColor: '#F8FAFC',
          },
          '& .MuiTableRow-root:hover': {
            backgroundColor: '#f5f5f5 !important',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          fontSize: '0.875rem',
          [theme.breakpoints.down('sm')]: {
            fontSize: '0.75rem',
          },
          [theme.breakpoints.between('sm', 'md')]: {
            fontSize: '0.8125rem',
          },
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ theme }) => ({
          '& .MuiFormLabel-asterisk': {
            color: theme.palette.error.main,
          },
        }),
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        asterisk: ({ theme }) => ({
          color: theme.palette.error.main,
        }),
      },
    },
  },
});
export default theme;
